import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { getBonus, updateBonus } from "../../services/bonusService";

const toastCfg = { autoClose: 1500 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Detail Modal ─── */
const EditBonusModal = ({ show, onHide, onSuccess, editData }) => {
  const [form, setForm] = useState({
    id: "",
    register_bonus: "",
    remark: "",
    status: 1,
  });

  useEffect(() => {
    if (show && editData) {
      setForm({
        id: editData.id,
        register_bonus: editData.register_bonus || "",
        remark: editData.remark || "",
        status: Number(editData.status),
      });
    }
  }, [show, editData]);

  const handleSubmit = async () => {
    if (!form.register_bonus) {
      toast.error("Bonus amount required", toastCfg);
      return;
    }

    try {
      const res = await updateBonus(form);
      if (res?.status === true) {
        toast.success("Bonus updated successfully", toastCfg);
        onSuccess();
        onHide();
      } else {
        toast.error("Update failed", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>Edit Bonus</div>
          <div style={ms.headerSub}>Update bonus details details</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={ms.body}>
        <div style={ms.grid}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Register Bonus</label>
            <input
              style={ms.input}
              type="number"
              placeholder="Enter Bonus Amount"
              value={form.register_bonus}
              onChange={(e) => setForm({ ...form, register_bonus: e.target.value })}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Remark</label>
            <input
              style={ms.input}
              placeholder="Enter Remark"
              value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Status</label>
            <select
              style={ms.select}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>
      </div>
      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide}>Cancel</button>
        <button style={ms.approveBtn} onClick={handleSubmit}>
          Save Changes
        </button>
      </div>
    </Modal>
  );
};

/* ─── Modal styles ─── */
const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub:   { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b",
  },
  body:  { padding: "18px 24px" },
  grid:  { display: "grid", gridTemplateColumns: "1fr", gap: "14px 18px" },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  select: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box",
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  approveBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer",
  },
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 260, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },
  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap", textAlign: "left",
  },
  td: {
    padding: "9px 14px", borderBottom: "1px solid #f8fafc",
    color: "#334155", verticalAlign: "middle",
  },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo:      { fontSize: "0.78rem", color: "#64748b" },
  pgBtns:      { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  wallet: { fontWeight: 700, color: "#16632b", fontSize: "0.83rem" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap", textTransform: "capitalize",
  },
  editBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px",
    background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
    fontSize: "0.75rem", fontWeight: 600,
  },
};

export const BonsusLayer = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  
  const [showModal, setShowModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const loadBonus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getBonus();
      if (res?.status === true) {
        setData(res.data || []);
      } else {
        toast.error("Failed to load bonus", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBonus();
  }, [loadBonus]);

  const openEditModal = (item) => {
    setActiveItem(item);
    setShowModal(true);
  };

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) => row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "register_bonus",
      header: "Bonus",
      cell: ({ getValue }) => (
        <span style={ts.wallet}>₹{Number(getValue() || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "remark",
      header: "Remark",
      cell: ({ getValue }) => <span>{getValue() || "—"}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const s = Number(getValue());
        return (
          <span style={{ 
            ...ts.badge, 
            background: s === 1 ? "#dcfce7" : "#fee2e2", 
            color: s === 1 ? "#16a34a" : "#dc2626" 
          }}>
            {s === 1 ? "● Active" : "○ Inactive"}
          </span>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Updated",
      cell: ({ getValue }) => <span style={{ color: "#64748b" }}>{getValue()}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      size: 90,
      enableSorting: false,
      cell: ({ row }) => (
        <button style={ts.editBtn} onClick={() => openEditModal(row.original)}>
          <Icon icon="solar:pen-bold" style={{ fontSize: 13 }} />
          Edit
        </button>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.remark?.toLowerCase().includes(v) ||
        String(row.original.register_bonus).includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const filteredDataLength = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Bonus Management</h4>
          <p style={s.pageSub}>View and update platform registration bonuses</p>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search by remark or bonus amount…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:gift-bold" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No bonuses match your search
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{filteredDataLength > 0 ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, filteredDataLength)}</strong>
            {" "}of <strong>{filteredDataLength}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const MathMax = Math.max;
              const MathMin = Math.min;
              const pg = MathMax(0, MathMin(pageIndex - 3, pageCount - 7)) + i;
              if (pg >= pageCount) return null;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <EditBonusModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        onSuccess={loadBonus} 
        editData={activeItem}
      />
    </div>
  );
};
